/**
 * Loads the Google tag (gtag.js: GA4 + Google Ads) ONLY after the visitor chose
 * "accept all" (cookie-consent === 'full'). Before that no request to Google is
 * made and window.gtag is undefined, so every tracking helper in
 * components/Analytics.tsx (which no-ops without window.gtag) stays silent.
 *
 * This used to be inline scripts in the server-rendered <head>. It is a client
 * effect on purpose: app/layout.tsx must stay static (no headers()/cookies()).
 *
 * The functions take the window as a parameter so they can be tested with a stub.
 */

export interface GoogleTagIds {
  ga4Id?: string;
  adsId?: string;
}

type GtagFn = (...args: unknown[]) => void;

interface GtagWindow {
  dataLayer?: unknown[];
  gtag?: GtagFn;
  __sendvcGtagLoaded?: boolean;
  document: Document;
  location: { hostname: string };
}

const GRANTED = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
};
const DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
};

/**
 * Defines window.gtag, sends the init calls and injects gtag.js. Idempotent:
 * calling it again while loaded does nothing; calling it again after
 * unloadGoogleTag() re-enables the already loaded script and grants consent.
 */
export function loadGoogleTag({ ga4Id, adsId }: GoogleTagIds, win: GtagWindow = window as unknown as GtagWindow): void {
  const primaryId = ga4Id || adsId;
  if (!primaryId || typeof win.gtag === 'function') return;

  win.dataLayer = win.dataLayer || [];
  const layer = win.dataLayer;
  // gtag.js only understands the `arguments` object, not an array of args.
  const gtag: GtagFn = function () { layer.push(arguments); };
  win.gtag = gtag;

  if (win.__sendvcGtagLoaded) {
    gtag('consent', 'update', GRANTED);
    return;
  }
  win.__sendvcGtagLoaded = true;

  gtag('js', new Date());
  // url_passthrough + ads_data_redaction stay: they keep the gclid usable on
  // internal navigation and strip ad identifiers from pings if consent is later
  // set back to denied.
  gtag('set', 'url_passthrough', true);
  gtag('set', 'ads_data_redaction', true);
  // Consent mode "basic": the tag does not exist before consent. It is loaded
  // here with a denied default followed at once by the update to granted.
  gtag('consent', 'default', DENIED);
  gtag('consent', 'update', GRANTED);
  if (ga4Id) gtag('config', ga4Id, { send_page_view: true });
  if (adsId) gtag('config', adsId);

  const script = win.document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`;
  win.document.head.appendChild(script);
}

/**
 * Cookies the Google tag / Ads set on our host: GA4 (_ga, _ga_<id>, _gid, _gat*),
 * Ads conversion linker (_gcl_au, _gcl_aw click id, _gcl_gs, _gcl_dc, _gcl_gb ...),
 * _gac_<id> and FPLC. Our own cookies never match.
 */
export function isGoogleTagCookie(name: string): boolean {
  return /^(_ga|_ga_.+|_gid|_gat.*|_gcl_.+|_gac_.+|FPLC)$/.test(name);
}

/**
 * Consent withdrawn: set consent to denied, make window.gtag undefined again so
 * no further event can be sent, and expire the Google cookies set on this host.
 */
export function unloadGoogleTag(win: GtagWindow = window as unknown as GtagWindow): void {
  if (typeof win.gtag === 'function') win.gtag('consent', 'update', DENIED);
  win.gtag = undefined;

  try {
    const host = win.location.hostname;
    const parent = host.split('.').length > 2 ? host.slice(host.indexOf('.')) : `.${host}`;
    const names = win.document.cookie
      .split(';')
      .map((c) => c.split('=')[0].trim())
      .filter(isGoogleTagCookie);
    for (const n of names) {
      for (const domain of [host, parent]) {
        win.document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
      }
      win.document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  } catch {
    // Cookie access blocked: nothing to remove.
  }
}
